<template>
  <TransitionRoot appear :show="open">
    <Dialog class="fixed inset-0 z-[60] overflow-y-auto" @close="close(true)">
      <TransitionChild
        as="template"
        enter="duration-250 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-250 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/30" />
      </TransitionChild>
      <TransitionChild
        as="template"
        enter="duration-250 ease-out"
        enter-from="opacity-0 scale-90"
        enter-to="opacity-100 scale-100"
        leave="duration-250 ease-in"
        leave-from="opacity-100 scale-100"
        leave-to="opacity-0 scale-90"
      >
        <DialogPanel
          data-modal-content
          class="flex min-h-screen items-center justify-center"
          :class="{ 'pl-0!': centerInWindow }"
        >
          <Card
            class="mx-2 my-24 max-h-[calc(100vh-120px)] flex-col items-center justify-center px-4 py-6 shadow-xl sm:mt-16 sm:px-6 md:mt-22"
            :class="{
              'overflow-visible': overflowVisible,
              'custom-table-scroll overflow-x-clip overflow-y-auto':
                !overflowVisible,
              'w-full sm:w-14/20 md:w-12/20 lg:w-10/20 xl:w-8/20 2xl:w-6/20':
                size === 'small',
              'w-full sm:w-16/20 md:w-14/20 lg:w-12/20 xl:w-10/20':
                size === 'medium',
              'w-full sm:w-18/20 md:w-16/20 lg:w-14/20 xl:w-12/20':
                size === 'large',
              'w-full max-w-375 sm:w-18/20 md:w-17/20 lg:w-16/20 xl:w-15/20 2xl:w-12/20':
                size === 'xlarge',
              'w-auto': size === 'auto',
            }"
          >
            <button
              v-if="useCloseButton"
              class="text-gray-700 transition duration-200 hover:text-gray-400"
              aria-label="Close modal"
              @click="close(false)"
            >
              <Icon
                name="times"
                class="absolute top-0 right-0 mt-7 mr-6 size-5"
              />
            </button>
            <DialogTitle
              v-if="$slots.header"
              class="mb-4 flex w-full flex-col text-lg font-medium md:text-xl"
              :class="borderless ? 'pb-0' : 'border-b border-gray-200 pb-4'"
            >
              <slot name="icon" />
              <slot name="header"> Header not set </slot>
            </DialogTitle>
            <div class="mb-4 w-full pt-2">
              <slot> Content not set </slot>
              <div
                v-show="errorMessages.length > 0"
                role="alert"
                class="w-full py-4"
              >
                <Message
                  v-for="(err, index) in errorMessages"
                  :key="index"
                  :message="err"
                />
              </div>
            </div>
            <div
              v-if="$slots.footer"
              class="flex w-full gap-2"
              :class="[
                centerFooter ? 'justify-center' : 'justify-end',
                borderless ? 'pt-0' : 'border-t border-gray-200 pt-4',
              ]"
            >
              <slot name="footer" />
            </div>
          </Card>
        </DialogPanel>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { PropType } from "vue";
import Card from "@/components/Core/Card.vue";
import Message from "@/components/Forms/Message.vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionRoot,
  TransitionChild,
} from "@headlessui/vue";
import { MessageContent } from "@/components/Forms/messages";
import Icon from "@/components/Core/Icon.vue";

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  size: {
    type: String as PropType<"auto" | "small" | "medium" | "large" | "xlarge">,
    default: "auto",
  },
  useCloseButton: {
    type: Boolean,
    default: false,
  },
  errorMessages: {
    type: Array as PropType<MessageContent[]>,
    default: () => [],
  },
  keepOpenOnClickOutside: { type: Boolean, default: false },
  /** Center on the window instead of the content area beside the sidebar. */
  centerInWindow: { type: Boolean, default: false },
  centerFooter: {
    type: Boolean,
    default: false,
  },
  overflowVisible: {
    type: Boolean,
    default: false,
  },
  borderless: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits({
  close: () => true,
});

function close(clickOutside?: boolean): void {
  function cls(): void {
    emit("close");
  }
  if (props.keepOpenOnClickOutside) {
    if (!clickOutside) cls();
  } else cls();
}
</script>
